import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Stack, Button, IconButton, TextField, MenuItem, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Tooltip, Snackbar, InputAdornment, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh, Search, AccountBalanceWallet, WarningAmberRounded, TrendingUp, Payments,
  Speed, PriceCheck, Payment, EventAvailable, ChatBubbleOutline, ArrowForward,
  ChevronRight, HourglassBottom,
} from '@mui/icons-material';
import { RouterLink } from '../../utils/linkNav';
import { collectionsService, ControlCenter, CollectionCustomer, AgingSummary } from '../../services/collectionsService';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import {
  StatCard, TableSurface, headerCellSx, bodyRowSx, StatusDot, StatusTone, TableEmptyState, ErrorState,
} from '../../components/common/pageKit';

const RISK_TONE: Record<string, StatusTone> = { HIGH: 'error', MEDIUM: 'warning', LOW: 'info', CURRENT: 'success' };
const RISK_TINT: Record<string, string> = { HIGH: '#dc2626', MEDIUM: '#d97706', LOW: '#0284c7', CURRENT: '#059669' };

type BucketKey = 'notDue' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90plus' | null;
const BUCKETS: { key: Exclude<BucketKey, null>; label: string; tint: string; test: (d: number) => boolean }[] = [
  { key: 'notDue', label: 'Current (not due)', tint: '#059669', test: (d) => d <= 0 },
  { key: 'd1_30', label: '1–30 days', tint: '#0284c7', test: (d) => d >= 1 && d <= 30 },
  { key: 'd31_60', label: '31–60 days', tint: '#d97706', test: (d) => d >= 31 && d <= 60 },
  { key: 'd61_90', label: '61–90 days', tint: '#ea580c', test: (d) => d >= 61 && d <= 90 },
  { key: 'd90plus', label: '90+ days', tint: '#dc2626', test: (d) => d > 90 },
];

const CollectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;

  const [data, setData] = useState<ControlCenter | null>(null);
  const [aging, setAging] = useState<AgingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [bucket, setBucket] = useState<BucketKey>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // dialogs
  const [payFor, setPayFor] = useState<CollectionCustomer | null>(null);
  const [promiseFor, setPromiseFor] = useState<CollectionCustomer | null>(null);
  const [activityFor, setActivityFor] = useState<CollectionCustomer | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([collectionsService.controlCenter(), collectionsService.aging()])
      .then(([cc, ag]) => { setData(cc); setAging(ag); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load collections'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [search, riskFilter, bucket]);

  const customers = useMemo(() => data?.customers || [], [data]);
  const filtered = useMemo(() => customers.filter((cu) => {
    if (riskFilter && cu.risk !== riskFilter) return false;
    if (search && !cu.customerName.toLowerCase().includes(search.toLowerCase())) return false;
    if (bucket) {
      const b = BUCKETS.find((x) => x.key === bucket)!;
      if (bucket === 'notDue') { if (cu.overdue > 0) return false; }
      else if (!b.test(cu.oldestDaysOverdue)) return false;
    }
    return true;
  }), [customers, riskFilter, search, bucket]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]);

  const k = data?.kpis;
  const brokenTotal = customers.reduce((s, cu) => s + cu.brokenPromises, 0);
  const openPromisesTotal = customers.reduce((s, cu) => s + cu.openPromises, 0);

  const kpis = k ? [
    { title: 'Total Receivables', color: '#4f46e5', icon: <AccountBalanceWallet />, value: formatIndianNumber(k.totalReceivables),
      footer: <Typography variant="caption" color="text.secondary">{k.openInvoiceCount} open · {k.customerCount} customers</Typography> },
    { title: 'Overdue', color: '#dc2626', icon: <WarningAmberRounded />, value: formatIndianNumber(k.overdue),
      onClick: () => { setBucket(null); setRiskFilter(''); setSearch(''); },
      footer: <Typography variant="caption" sx={{ color: c.bad, fontWeight: 700 }}>{k.totalReceivables ? Math.round((k.overdue / k.totalReceivables) * 100) : 0}% of book</Typography> },
    { title: 'Collected This Month', color: '#059669', icon: <Payments />, value: formatIndianNumber(k.collectedThisMonth),
      footer: <Stack direction="row" spacing={0.5} alignItems="center"><TrendingUp sx={{ fontSize: 15, color: c.good }} /><Typography variant="caption" color="text.secondary">{k.receiptsThisMonth} receipts</Typography></Stack> },
    { title: 'Collection Rate', color: '#0284c7', icon: <PriceCheck />, value: k.collectionRate != null ? `${k.collectionRate}%` : null,
      share: k.collectionRate != null ? k.collectionRate / 100 : undefined },
    { title: 'DSO', color: '#7c3aed', icon: <Speed />, value: k.dso != null ? `${Math.round(k.dso)}d` : null,
      footer: <Typography variant="caption" color="text.secondary">Days sales outstanding</Typography> },
    { title: 'High-Risk Exposure', color: '#b91c1c', icon: <WarningAmberRounded />, value: formatIndianNumber(k.highRiskExposure),
      onClick: () => setRiskFilter('HIGH'),
      footer: <Typography variant="caption" color="text.secondary">{k.highRiskCount} customers at risk</Typography> },
  ] : [];

  const agingTotal = aging?.total || 0;

  const runAction = async (fn: () => Promise<any>, msg: string) => {
    try { await fn(); setToast(msg); load(); }
    catch (e: any) { setToast(e.message || 'Action failed'); throw e; }
  };

  return (
    <Box sx={{ p: 3, bgcolor: c.bg, minHeight: '100%' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Receivables & Collections</Typography>
          <Typography variant="body2" color="text.secondary">Live AR control room — outstanding, ageing, risk and collection workflow across every customer.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
      </Stack>

      {/* KPI strip */}
      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        {(k ? kpis : Array.from({ length: 6 })).map((kp: any, i) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={i}>
            {k
              ? <StatCard title={kp.title} value={kp.value} icon={kp.icon} color={kp.color} share={kp.share} footer={kp.footer} onClick={kp.onClick} />
              : <Paper elevation={0} sx={{ ...panelSx, height: 118, display: 'grid', placeItems: 'center' }}><CircularProgress size={20} /></Paper>}
          </Grid>
        ))}
      </Grid>

      {/* aging bar + exceptions */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Ageing of Receivables</Typography>
              {bucket && <Button size="small" onClick={() => setBucket(null)}>Clear filter</Button>}
            </Stack>
            {!aging ? <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={20} /></Box> : (
              <>
                <Box sx={{ display: 'flex', width: '100%', height: 34, borderRadius: 1.5, overflow: 'hidden', border: line }}>
                  {BUCKETS.map((b) => {
                    const amt = (aging.buckets as any)[b.key] as number;
                    const pct = agingTotal > 0 ? (amt / agingTotal) * 100 : 0;
                    if (pct <= 0) return null;
                    return (
                      <Tooltip key={b.key} title={`${b.label}: ${formatCurrency(amt)} (${Math.round(pct)}%)`}>
                        <Box onClick={() => setBucket(bucket === b.key ? null : b.key)}
                          sx={{ width: `${pct}%`, bgcolor: b.tint, cursor: 'pointer', opacity: bucket && bucket !== b.key ? 0.35 : 1,
                            transition: 'opacity .2s', '&:hover': { opacity: 0.85 } }} />
                      </Tooltip>
                    );
                  })}
                </Box>
                <Grid container spacing={1} sx={{ mt: 1.5 }}>
                  {BUCKETS.map((b) => {
                    const amt = (aging.buckets as any)[b.key] as number;
                    const active = bucket === b.key;
                    return (
                      <Grid size={{ xs: 6, sm: 2.4 }} key={b.key}>
                        <Box onClick={() => setBucket(active ? null : b.key)}
                          sx={{ p: 1, borderRadius: 2, border: active ? `1.5px solid ${b.tint}` : line, cursor: 'pointer',
                            bgcolor: active ? `${b.tint}12` : 'transparent', '&:hover': { bgcolor: `${b.tint}0d` } }}>
                          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
                            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: b.tint, flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', lineHeight: 1.1 }}>{b.label}</Typography>
                          </Stack>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(amt)}</Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1.5 }}>Exceptions</Typography>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ p: 1.25, borderRadius: 2, border: line, cursor: 'pointer', '&:hover': { bgcolor: c.panel2 } }}
                onClick={() => { setBucket(null); setRiskFilter(''); }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.bad, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Overdue receivables</Typography>
                  <Typography variant="caption" color="text.secondary">{formatIndianNumber(k?.overdue || 0)} past due</Typography>
                </Box>
                <ChevronRight sx={{ fontSize: 18, color: c.sub }} />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ p: 1.25, borderRadius: 2, border: line }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: brokenTotal ? c.bad : c.good, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Broken promises</Typography>
                  <Typography variant="caption" color="text.secondary">{brokenTotal} broken · {openPromisesTotal} open</Typography>
                </Box>
                <HourglassBottom sx={{ fontSize: 18, color: brokenTotal ? c.bad : c.sub }} />
              </Stack>
              {k?.topDebtor && (
                <Stack direction="row" alignItems="center" spacing={1.25} component={RouterLink} to={`/finance/collections/${k.topDebtor!.customerId}`} sx={{ p: 1.25, borderRadius: 2, border: line, cursor: 'pointer', textDecoration: 'none', color: 'inherit', '&:hover': { bgcolor: c.panel2 } }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.warn, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Top debtor: {k.topDebtor.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatIndianNumber(k.topDebtor.amount)} outstanding</Typography>
                  </Box>
                  <ArrowForward sx={{ fontSize: 18, color: c.sub }} />
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* filters */}
      <Paper elevation={0} sx={{ ...panelSx, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search customer…" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, flexGrow: { xs: 1, md: 0 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: c.sub }} /></InputAdornment> }} />
          <TextField select size="small" label="Risk" value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">All risk levels</MenuItem>
            {['HIGH', 'MEDIUM', 'LOW', 'CURRENT'].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          {['HIGH', 'MEDIUM', 'LOW'].map((r) => (
            <Chip key={r} size="small" label={r} clickable variant={riskFilter === r ? 'filled' : 'outlined'}
              onClick={() => setRiskFilter(riskFilter === r ? '' : r)}
              sx={{ borderColor: c.line, ...(riskFilter === r ? { bgcolor: `${RISK_TINT[r]}1f`, color: RISK_TINT[r] } : {}) }} />
          ))}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary"><b>{filtered.length}</b> {filtered.length === 1 ? 'customer' : 'customers'}</Typography>
        </Box>
      </Paper>

      {error && <Box sx={{ mb: 2 }}><ErrorState message={error} onRetry={load} /></Box>}

      {/* customer table */}
      <TableSurface>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {['Customer', 'Outstanding', 'Current', 'Overdue', 'Days', 'Credit Limit', 'Available', 'Last Payment', 'Risk', 'Promises', 'Actions'].map((h, i) => (
                  <TableCell key={h} sx={headerCellSx} align={i >= 1 && i <= 6 ? 'right' : 'left'}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && customers.length === 0 ? (
                <TableRow><TableCell colSpan={11} align="center" sx={{ border: 0, py: 6 }}><CircularProgress /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={11} sx={{ border: 0 }}>
                  <TableEmptyState title="No customers match these filters" hint="Adjust the risk filter or ageing bucket to widen the view." />
                </TableCell></TableRow>
              ) : paged.map((cu) => (
                <TableRow key={cu.customerId} hover sx={{ ...(bodyRowSx as any), cursor: 'pointer' }}
                  onClick={() => navigate(`/finance/collections/${cu.customerId}`)}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{cu.customerName}</Typography>
                    <Typography variant="caption" color="text.secondary">{cu.openInvoiceCount} open invoice{cu.openInvoiceCount === 1 ? '' : 's'}{cu.gstin ? ` · ${cu.gstin}` : ''}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatIndianNumber(cu.outstanding)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: c.good }}>{formatIndianNumber(cu.current)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: cu.overdue > 0 ? c.bad : 'text.secondary', fontWeight: cu.overdue > 0 ? 700 : 400 }}>{formatIndianNumber(cu.overdue)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cu.oldestDaysOverdue > 0 ? cu.oldestDaysOverdue : '—'}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{cu.creditLimit > 0 ? formatIndianNumber(cu.creditLimit) : '—'}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: cu.availableCredit < 0 ? c.bad : 'text.secondary' }}>{cu.creditLimit > 0 ? formatIndianNumber(cu.availableCredit) : '—'}</TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{cu.lastPaymentDate ? new Date(cu.lastPaymentDate).toLocaleDateString('en-IN') : 'Never'}</Typography></TableCell>
                  <TableCell><StatusDot label={cu.risk} tone={RISK_TONE[cu.risk] || 'neutral'} /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {cu.openPromises > 0 && <Chip size="small" label={`${cu.openPromises} open`} sx={{ height: 20, fontSize: '0.65rem', bgcolor: `${c.accent}14`, color: c.accent }} />}
                      {cu.brokenPromises > 0 && <Chip size="small" label={`${cu.brokenPromises} broken`} sx={{ height: 20, fontSize: '0.65rem', bgcolor: `${c.bad}14`, color: c.bad }} />}
                      {cu.openPromises === 0 && cu.brokenPromises === 0 && <Typography variant="caption" color="text.secondary">—</Typography>}
                    </Stack>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.25}>
                      <Tooltip title="Record payment"><IconButton size="small" onClick={() => setPayFor(cu)}><Payment sx={{ fontSize: 17, color: c.good }} /></IconButton></Tooltip>
                      <Tooltip title="Promise to pay"><IconButton size="small" onClick={() => setPromiseFor(cu)}><EventAvailable sx={{ fontSize: 17, color: c.accent }} /></IconButton></Tooltip>
                      <Tooltip title="Log activity"><IconButton size="small" onClick={() => setActivityFor(cu)}><ChatBubbleOutline sx={{ fontSize: 17, color: c.sub }} /></IconButton></Tooltip>
                      <Tooltip title="Open 360"><IconButton size="small" component={RouterLink} to={`/finance/collections/${cu.customerId}`}><ArrowForward sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {filtered.length > 0 && (
          <TablePagination component="div" count={filtered.length} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50, 100]} />
        )}
      </TableSurface>

      {payFor && <RecordPaymentDialog customer={payFor} onClose={() => setPayFor(null)}
        onSubmit={(d) => runAction(() => collectionsService.recordPayment(d), 'Payment recorded').then(() => setPayFor(null))} />}
      {promiseFor && <PromiseDialog customer={promiseFor} onClose={() => setPromiseFor(null)}
        onSubmit={(d) => runAction(() => collectionsService.createPromise(d), 'Promise to pay recorded').then(() => setPromiseFor(null))} />}
      {activityFor && <ActivityDialog customer={activityFor} onClose={() => setActivityFor(null)}
        onSubmit={(d) => runAction(() => collectionsService.logActivity(d), 'Activity logged').then(() => setActivityFor(null))} />}

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Dialogs (exported so the 360 page can reuse them)
// ---------------------------------------------------------------------------

interface InvoiceOpt { id: number; invoiceNumber?: string; invoice_number?: string; outstanding: number }

export const RecordPaymentDialog: React.FC<{
  customer: { customerId: number; customerName: string };
  invoices?: InvoiceOpt[];
  onClose: () => void;
  onSubmit: (d: { invoiceId: number; amount: number; date: string; method: string; reference: string }) => Promise<any>;
}> = ({ customer, invoices, onClose, onSubmit }) => {
  const [invoiceId, setInvoiceId] = useState<number | ''>('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // When the caller has not already loaded this customer's open invoices, the
  // dialog fetches them itself. It used to fall back to a free-text "Invoice
  // ID" number box with the helper text "Open the customer 360 to pick an
  // invoice from the list" — i.e. it asked the user to go and look up a
  // database id and type it back in. The endpoint to list them already
  // existed; nothing was calling it here.
  const [fetched, setFetched] = useState<InvoiceOpt[] | null>(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    if (invoices && invoices.length) return;
    setLoadingInvoices(true);
    collectionsService.getCustomerOpenInvoices(customer.customerId)
      .then((r) => setFetched((r.invoices || []) as unknown as InvoiceOpt[]))
      .catch(() => setFetched([]))
      .finally(() => setLoadingInvoices(false));
  }, [customer.customerId, invoices]);

  const options = (invoices && invoices.length ? invoices : fetched) || [];

  // Selecting an invoice pre-fills the amount with what is actually
  // outstanding on it — the number the user would otherwise copy across.
  const onPickInvoice = (id: number) => {
    setInvoiceId(id);
    const picked = options.find((o) => o.id === id);
    if (picked && Number(picked.outstanding) > 0) setAmount(String(picked.outstanding));
  };

  const submit = async () => {
    if (!invoiceId) { setErr('Select an invoice'); return; }
    if (!(Number(amount) > 0)) { setErr('Enter an amount'); return; }
    setBusy(true); setErr(null);
    try { await onSubmit({ invoiceId: Number(invoiceId), amount: Number(amount), date, method, reference }); }
    catch (e: any) { setErr(e.message || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Record Payment — {customer.customerName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            select label="Invoice" value={invoiceId} size="small" fullWidth
            onChange={(e) => onPickInvoice(Number(e.target.value))}
            disabled={loadingInvoices || options.length === 0}
            helperText={
              loadingInvoices ? 'Loading this customer’s open invoices…'
                : options.length === 0 ? 'This customer has no open invoices.'
                  : 'Picking an invoice fills in the outstanding amount.'
            }
          >
            {options.map((iv) => (
              <MenuItem key={iv.id} value={iv.id}>
                {(iv.invoiceNumber || iv.invoice_number)} — {formatCurrency(iv.outstanding)} due
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} size="small" fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          <TextField label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} />
          <TextField select label="Method" value={method} onChange={(e) => setMethod(e.target.value)} size="small" fullWidth>
            {['BANK_TRANSFER', 'CASH', 'CHEQUE', 'UPI', 'CARD'].map((m) => <MenuItem key={m} value={m}>{m.replace('_', ' ')}</MenuItem>)}
          </TextField>
          <TextField label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} size="small" fullWidth />
          {err && <Typography variant="caption" color="error">{err}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy} startIcon={busy ? <CircularProgress size={15} /> : <Payment />}>Record Payment</Button>
      </DialogActions>
    </Dialog>
  );
};

export const PromiseDialog: React.FC<{
  customer: { customerId: number; customerName: string };
  invoices?: InvoiceOpt[];
  onClose: () => void;
  onSubmit: (d: { customerId: number; invoiceId?: number | null; promisedAmount: number; promisedDate: string; notes: string }) => Promise<any>;
}> = ({ customer, invoices, onClose, onSubmit }) => {
  const [invoiceId, setInvoiceId] = useState<number | ''>('');
  const [amount, setAmount] = useState('');
  const [promisedDate, setPromisedDate] = useState(new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!(Number(amount) > 0)) { setErr('Enter an amount'); return; }
    if (!promisedDate) { setErr('Pick a date'); return; }
    setBusy(true); setErr(null);
    try { await onSubmit({ customerId: customer.customerId, invoiceId: invoiceId || null, promisedAmount: Number(amount), promisedDate, notes }); }
    catch (e: any) { setErr(e.message || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Promise to Pay — {customer.customerName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {invoices && invoices.length > 0 && (
            <TextField select label="Invoice (optional)" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value === '' ? '' : Number(e.target.value))} size="small" fullWidth>
              <MenuItem value="">Account-level (any invoice)</MenuItem>
              {invoices.map((iv) => <MenuItem key={iv.id} value={iv.id}>{(iv.invoiceNumber || iv.invoice_number)} — {formatCurrency(iv.outstanding)} due</MenuItem>)}
            </TextField>
          )}
          <TextField label="Promised amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} size="small" fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          <TextField label="Promised date" type="date" value={promisedDate} onChange={(e) => setPromisedDate(e.target.value)} size="small" fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} size="small" fullWidth multiline minRows={2} />
          {err && <Typography variant="caption" color="error">{err}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy} startIcon={busy ? <CircularProgress size={15} /> : <EventAvailable />}>Record Promise</Button>
      </DialogActions>
    </Dialog>
  );
};

export const ActivityDialog: React.FC<{
  customer: { customerId: number; customerName: string };
  invoices?: InvoiceOpt[];
  onClose: () => void;
  onSubmit: (d: { customerId: number; invoiceId?: number | null; activityType: string; notes: string; outcome: string }) => Promise<any>;
}> = ({ customer, invoices, onClose, onSubmit }) => {
  const [activityType, setActivityType] = useState('CALL');
  const [invoiceId, setInvoiceId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [outcome, setOutcome] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try { await onSubmit({ customerId: customer.customerId, invoiceId: invoiceId || null, activityType, notes, outcome }); }
    catch (e: any) { setErr(e.message || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Log Activity — {customer.customerName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField select label="Type" value={activityType} onChange={(e) => setActivityType(e.target.value)} size="small" fullWidth>
            {['CALL', 'EMAIL', 'NOTE', 'REMINDER', 'ESCALATION'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          {invoices && invoices.length > 0 && (
            <TextField select label="Invoice (optional)" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value === '' ? '' : Number(e.target.value))} size="small" fullWidth>
              <MenuItem value="">None</MenuItem>
              {invoices.map((iv) => <MenuItem key={iv.id} value={iv.id}>{(iv.invoiceNumber || iv.invoice_number)}</MenuItem>)}
            </TextField>
          )}
          <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} size="small" fullWidth multiline minRows={2} />
          <TextField label="Outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)} size="small" fullWidth placeholder="e.g. Promised payment by Friday" />
          {err && <Typography variant="caption" color="error">{err}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy} startIcon={busy ? <CircularProgress size={15} /> : <ChatBubbleOutline />}>Log Activity</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectionsPage;
